import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import {
  AddFlowEtfSchema,
  type AddFlowEtfInput,
  type FlowEtfDto,
  type MarketFlowUniverse,
} from 'shared';
import { toast } from 'sonner';

import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { Label } from '../components/ui/Label';
import {
  useAddFlowEtf,
  useRemoveFlowEtf,
  useResetFlowEtfs,
} from '../features/marketConsole/mutations';
import { useFlowEtfs } from '../features/marketConsole/queries';

const UNIVERSE_LABELS: Record<MarketFlowUniverse, string> = {
  THEME: 'Themes',
  SECTOR: 'Sectors',
};

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function groupsOf(etfs: readonly FlowEtfDto[]): Array<[string, FlowEtfDto[]]> {
  const groups = new Map<string, FlowEtfDto[]>();
  for (const etf of etfs) groups.set(etf.group, [...(groups.get(etf.group) ?? []), etf]);
  return [...groups].sort(([left], [right]) => left.localeCompare(right));
}

function AddEtfForm({ etfs }: { etfs: readonly FlowEtfDto[] }) {
  const add = useAddFlowEtf();
  const form = useForm<AddFlowEtfInput>({
    resolver: zodResolver(AddFlowEtfSchema),
    defaultValues: { symbol: '', universe: 'THEME', group: '', description: '' },
  });
  const universe = form.watch('universe');
  const groupNames = [
    ...new Set(etfs.filter((etf) => etf.universe === universe).map((etf) => etf.group)),
  ].sort();

  const submit = form.handleSubmit((input) => {
    add.mutate(input, {
      onSuccess: (etf) => {
        toast.success(`${etf.symbol} added to ${UNIVERSE_LABELS[etf.universe]}.`);
        form.reset({ symbol: '', universe: input.universe, group: input.group, description: '' });
      },
      onError: (error) => toast.error(errorText(error, `Could not add ${input.symbol}.`)),
    });
  });

  return (
    <form className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5 lg:items-end" onSubmit={submit}>
      <FormField
        label="Ticker"
        placeholder="e.g. SMH"
        autoCapitalize="characters"
        error={form.formState.errors.symbol?.message}
        {...form.register('symbol')}
      />
      <div className="space-y-2">
        <Label htmlFor="flow-etf-universe">List</Label>
        <select
          id="flow-etf-universe"
          className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
          {...form.register('universe')}
        >
          <option value="THEME">Themes</option>
          <option value="SECTOR">Sectors</option>
        </select>
      </div>
      <FormField
        label="Group"
        placeholder="e.g. Semiconductors"
        list="flow-etf-groups"
        error={form.formState.errors.group?.message}
        {...form.register('group')}
      />
      <datalist id="flow-etf-groups">
        {groupNames.map((group) => (
          <option key={group} value={group} />
        ))}
      </datalist>
      <FormField
        label="Description (optional)"
        placeholder="Fund name"
        error={form.formState.errors.description?.message}
        {...form.register('description')}
      />
      <Button type="submit" disabled={add.isPending}>
        {add.isPending ? 'Checking ticker…' : 'Add ETF'}
      </Button>
    </form>
  );
}

function UniverseList({
  universe,
  etfs,
}: {
  universe: MarketFlowUniverse;
  etfs: readonly FlowEtfDto[];
}) {
  const remove = useRemoveFlowEtf();
  const inUniverse = etfs.filter((etf) => etf.universe === universe);
  return (
    <Card
      header={
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold">{UNIVERSE_LABELS[universe]}</h2>
          <span className="text-xs text-text-muted">{inUniverse.length} ETFs</span>
        </div>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {groupsOf(inUniverse).map(([group, members]) => (
          <section
            key={group}
            className="min-w-0"
            aria-label={`${UNIVERSE_LABELS[universe]}: ${group}`}
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {group}
            </h3>
            <ul className="mt-1 divide-y divide-border">
              {members.map((etf) => (
                <li key={etf.id} className="flex items-center gap-2 py-1.5 text-sm">
                  <span className="w-14 font-mono font-semibold">{etf.symbol}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-text-muted">
                    {etf.description}
                  </span>
                  {etf.dataStatus === 'PENDING' ? <Badge tone="warning">Pending</Badge> : null}
                  <button
                    type="button"
                    aria-label={`Remove ${etf.symbol} from ${UNIVERSE_LABELS[universe]}`}
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(etf.id, {
                        onError: (error) =>
                          toast.error(errorText(error, `Could not remove ${etf.symbol}.`)),
                      })
                    }
                    className="rounded p-1.5 text-text-muted hover:bg-surface-raised hover:text-danger disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Card>
  );
}

export function FlowEtfSettings() {
  const etfs = useFlowEtfs();
  const reset = useResetFlowEtfs();
  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <main className="min-h-screen bg-bg px-4 py-6 text-text sm:px-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="space-y-2">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Settings
          </Link>
          <h1 className="text-2xl font-semibold">Flow ETF lists</h1>
          <p className="text-sm text-text-muted">
            The theme and sector ETFs ranked on Market Console → Flow. ETFs in the same group are
            scored together. New ETFs show as pending until the next ETF flow refresh.
          </p>
        </header>

        {etfs.isLoading ? (
          <p className="text-sm text-text-muted">Loading ETF lists…</p>
        ) : etfs.isError || !etfs.data ? (
          <p className="text-sm text-danger">ETF lists could not be loaded.</p>
        ) : (
          <>
            <Card header={<h2 className="text-lg font-semibold">Add an ETF</h2>}>
              <AddEtfForm etfs={etfs.data.etfs} />
            </Card>
            <UniverseList universe="THEME" etfs={etfs.data.etfs} />
            <UniverseList universe="SECTOR" etfs={etfs.data.etfs} />
            <Card header={<h2 className="text-lg font-semibold">Reset to defaults</h2>}>
              <p className="text-sm text-text-muted">
                Replaces both lists with the starting theme and sector ETFs.
              </p>
              <div className="mt-3 flex gap-2">
                {confirmReset ? (
                  <>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={reset.isPending}
                      onClick={() =>
                        reset.mutate(undefined, {
                          onSuccess: () => {
                            setConfirmReset(false);
                            toast.success('ETF lists reset to defaults.');
                          },
                          onError: (error) => toast.error(errorText(error, 'Reset failed.')),
                        })
                      }
                    >
                      Confirm reset
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmReset(false)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmReset(true)}
                  >
                    Reset to defaults
                  </Button>
                )}
              </div>
            </Card>
          </>
        )}
      </div>
    </main>
  );
}
